import { Box, Typography } from "@mui/material";
import { MRT_PaginationState, MRT_SortingState, MRT_ColumnFiltersState } from "material-react-table";
import { PrimaryGrid } from "src/components/Grid/layout/PrimaryGrid";
import { Dispatch, SetStateAction } from "react";
import { DuplicatePageDialog } from "./DuplicatePageDialog";
import { RolesDialog } from "./RolesDialog";
import { PageSettingsDialog } from "./PageSettingsDialog";
import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";
import { useCMSEditorPagesTableController } from "../hooks/useCMSEditorPagesTableController";
import { ScoreCompanyDialog } from "./ScoreCompanyDialog";

type CMSEditorPagesTableProps = {
    pages: PuckPage[];
    title?: string;
    onEdit: (slug: string) => void;
    getEditUrl?: (slug: string) => string;
    pageType?: "CMS" | "PDF";
    onDelete: (slug: string) => void;
    onUpdateTitle: (slug: string, title: string) => void;
    onUpdateRoles?: (slug: string, roles: string[]) => void;
    onUpdateStatus?: (slug: string, status: "active" | "draft") => void;
    onUpdateMeta?: (
        slug: string,
        payload: {
            url?: string;
            sectors?: string[] | null;
            allowedRoles?: string[];
            status?: "active" | "draft";
            title?: string;
            description?: string;
            downloadOnlyOnSsqValidated?: boolean;
            score?: string | string[];
            entity?: string | string[];
        },
    ) => Promise<unknown> | void;
    onDuplicatePage?: (source: PuckPage, payload: { title: string; url: string }) => Promise<unknown> | void;
    pagination: MRT_PaginationState;
    setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
    sorting: MRT_SortingState;
    setSorting: Dispatch<SetStateAction<MRT_SortingState>>;
    columnFilters: MRT_ColumnFiltersState;
    setColumnFilters: Dispatch<SetStateAction<MRT_ColumnFiltersState>>;
    totalItems: number;
    isServerSide: boolean;
    isCreating?: boolean;
};

export const CMSEditorPagesTable = (props: CMSEditorPagesTableProps) => {
    const {
        pages,
        title,
        onEdit,
        getEditUrl,
        pageType = "CMS",
        onDelete,
        onUpdateTitle,
        onUpdateRoles,
        onUpdateStatus,
        onUpdateMeta,
        onDuplicatePage,
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        totalItems,
        isServerSide,
    } = props;

    const controller = useCMSEditorPagesTableController({
        pages,
        onEdit,
        getEditUrl,
        pageType,
        onDelete,
        onUpdateTitle,
        onUpdateRoles,
        onUpdateStatus,
        onUpdateMeta,
        onDuplicatePage,
        isServerSide,
        isCreating: props.isCreating,
    });

    return (
        <Box
            sx={{
                "& [class*='MuiButton-tags']": {
                    height: "unset",
                    textAlign: "left",
                },
            }}
        >
            {title && (
                <Typography variant="h3" sx={{ mb: controller.theme.spacing(4) }}>
                    {title}
                </Typography>
            )}
            <PrimaryGrid
                enableDynamicRowHeight
                enableViews
                data={pages}
                columns={controller.mrt_columns.mrt_columns}
                gridKey={controller.cmsEditorPagesTableKey}
                menuItems={controller.menuItems}
                emptyMessage={controller.emptyMessage}
                filters={{
                    columnFilters: columnFilters,
                    setColumnFilters: setColumnFilters,
                }}
                sort={{
                    sorting: sorting,
                    setSorting: setSorting,
                }}
                pagination={{
                    pagination: pagination,
                    setPagination: setPagination,
                    totalItems: totalItems,
                }}
                enableServerSidePagination={isServerSide}
                enableServerSideSorting={isServerSide}
                enableServerSideFiltering={isServerSide}
                columnConfig={controller.mrt_columns}
                onRowClick={controller.canModifyPage ? (page: PuckPage) => onEdit(page.slug) : undefined}
                enableRowActions={controller.menuItems.length > 0}
            />
            <RolesDialog
                open={controller.rolesDialog.open}
                pageTitle={controller.rolesDialog.pageTitle}
                selectedRoles={controller.rolesDialog.selectedRoles}
                onChange={controller.rolesDialog.setSelectedRoles}
                onClose={controller.rolesDialog.onClose}
                onSave={controller.rolesDialog.onSave}
                isSaving={controller.rolesDialog.isSaving}
            />

            <ScoreCompanyDialog
                open={controller.scoreCompanyDialog.open}
                onClose={controller.scoreCompanyDialog.onClose}
                selectedScore={controller.scoreCompanyDialog.selectedScore}
                onChangeScore={controller.scoreCompanyDialog.setSelectedScore}
                selectedEntity={controller.scoreCompanyDialog.selectedEntity}
                onChangeEntity={controller.scoreCompanyDialog.setSelectedEntity}
                scores={controller.scoreCompanyDialog.scores}
                onSave={controller.scoreCompanyDialog.onSave}
                isSaving={controller.scoreCompanyDialog.isSaving}
            />

            {controller.settingsDialog.page && (
                <PageSettingsDialog
                    open={controller.settingsDialog.open}
                    page={controller.settingsDialog.page}
                    pageType={controller.settingsDialog.pageType}
                    settingsTitle={controller.settingsDialog.settingsTitle}
                    settingsDescription={controller.settingsDialog.settingsDescription}
                    settingsUrl={controller.settingsDialog.settingsUrl}
                    settingsRoles={controller.settingsDialog.settingsRoles}
                    settingsActive={controller.settingsDialog.settingsActive}
                    settingsSectorIds={controller.settingsDialog.settingsSectorIds}
                    settingsScore={controller.settingsDialog.settingsScore}
                    settingsEntity={controller.settingsDialog.settingsEntity}
                    settingsDownloadOnlyOnSsqValidated={controller.settingsDialog.settingsDownloadOnlyOnSsqValidated}
                    allSectors={controller.settingsDialog.allSectors}
                    scores={controller.settingsDialog.scores}
                    disabled={controller.settingsDialog.disabled}
                    urlError={controller.settingsDialog.urlError}
                    urlHelperText={controller.settingsDialog.urlHelperText}
                    saveDisabled={controller.settingsDialog.saveDisabled}
                    onChangeTitle={controller.settingsDialog.onChangeTitle}
                    onChangeDescription={controller.settingsDialog.onChangeDescription}
                    onChangeUrl={controller.settingsDialog.onChangeUrl}
                    onChangeActive={controller.settingsDialog.onChangeActive}
                    onChangeRoles={controller.settingsDialog.onChangeRoles}
                    onChangeSectorIds={controller.settingsDialog.onChangeSectorIds}
                    onChangeScore={controller.settingsDialog.onChangeScore}
                    onChangeEntity={controller.settingsDialog.onChangeEntity}
                    onChangeDownloadOnlyOnSsqValidated={controller.settingsDialog.onChangeDownloadOnlyOnSsqValidated}
                    onClose={controller.settingsDialog.onClose}
                    onSave={controller.settingsDialog.onSave}
                    saving={controller.settingsDialog.saving}
                />
            )}

            <DuplicatePageDialog
                open={controller.duplicateDialog.open}
                sourcePage={controller.duplicateDialog.sourcePage}
                onClose={controller.duplicateDialog.onClose}
                isLoading={controller.duplicateDialog.isLoading}
                onConfirm={controller.duplicateDialog.onConfirm}
            />
        </Box>
    );
};
